/**
 * Help section copy (English). Merged into i18n as help.sections.<id>.<key>
 * Use **text** in strings for bold segments (see HelpText in HelpSections).
 */
export type HelpSectionMap = Record<string, Record<string, string>>;

const helpSectionsEn: HelpSectionMap = {
  'getting-started': {
    intro:
      'Here’s everything you need to get going. Follow these steps or explore the sidebar.',
    stepsTitle: 'Your first steps',
    step1: 'Open **Overview** to see your dashboard and recent activity.',
    step2: 'Open **My Tasks** to see all issues assigned to you.',
    step3: 'Click a **Project** in the sidebar to open its board, backlog, roadmap, and more.',
    stepPm:
      'Open **Project Management** to see all projects and a roadmap overview (current stage, next deployment, planned date).',
    step4: 'Use the header **Create** button to add a new issue.',
    step5:
      'Use the **theme toggle** (sun/moon icon) in the header to switch between light and dark mode.',
    outro:
      'Use the links on the left to jump to any topic, or take the quick tour above for a short walkthrough.',
  },
  notifications: {
    intro:
      'The bell icon in the header shows your in-app notifications. Click it to open the notification center and mark items as read.',
    whatTitle: 'What you get notified about',
    n1: 'When someone assigns you an issue',
    n2: 'When you’re @mentioned in a comment',
    n3: 'Status changes, comments, and due date reminders',
    n4: 'Project invites (if your org uses them)',
    prefsTitle: 'Managing preferences',
    prefsBody:
      'Go to your profile or account settings to choose which notifications you get by email and in-app (e.g. only assignments, or mentions too).',
    sessionsBody:
      'From your profile you can also view **Active sessions** and sign out from other devices.',
    tip: 'Open the bell and click “Mark all as read” to clear the list.',
  },
  'keyboard-shortcuts': {
    intro:
      'Use these from anywhere in the app when the main content is focused. Use **Cmd** on Mac and **Ctrl** on Windows/Linux.',
    k1: '**Cmd/Ctrl + K** or **/** – Search issues and projects (spotlight). Disabled when an issue modal is open.',
    k2: '**Cmd/Ctrl + I** – Open Create Issue',
    k3: '**Cmd/Ctrl + Enter** – Submit the Create Issue form',
    k4: '**Cmd/Ctrl + S** – Save (in Doc editor)',
    k5: '**Escape** – Close a modal or search',
    tip: 'Press / or Cmd/Ctrl + K to find any issue or project without leaving the page. List view uses virtualized scrolling for large lists.',
  },
  projects: {
    intro:
      'Projects hold your issues, sprints, and reports. You can open any project you’re a member of from the sidebar.',
    createTitle: 'How to create a project',
    createBody:
      'Go to **Project Management** and click **Create Project**. Add a name, description, and category. You can use a template to prefill workflows and settings.',
    membersTitle: 'Managing members',
    membersLead:
      'Open **Project Management** to see all projects, export to CSV, and a **Roadmap overview** (current stage, next deployment, planned date per project). Open a project, then use **Manage members** for that project to:',
    m1: 'Add people and set their role (Admin, Member, or Viewer).',
    m2:
      'Add a **group** (from Admin Settings → Groups) so everyone in the group gets the same role.',
    m3: 'Change a role or remove someone.',
    rolesHint:
      'Viewer = view only. Member = edit issues and comment. Admin = manage members and settings. You need at least one project admin.',
    notePlan:
      'Project creation may be limited by your organization’s plan (Free, Pro, Enterprise). See Plans & limits below.',
    memberOnly:
      'Only admins and project managers can create projects. If you need a new project, ask your admin or use the request-access flow.',
  },
  issues: {
    intro:
      'Issues are the work items you track: tasks, bugs, stories, epics, and sub-tasks.',
    createTitle: 'How to create an issue',
    c1: 'Click **Create** in the header.',
    c2: 'Choose **Create issue** or **Bulk create from CSV**.',
    c3: 'Fill in the form (project, title, type, due date, etc.) and submit.',
    createTip: 'You can also create issues from a project board or the backlog.',
    typesTitle: 'Issue types',
    typeTask: '**Task** – General work item',
    typeStory: '**Story** – User-facing feature',
    typeBug: '**Bug** – Something to fix',
    typeEpic: '**Epic** – Big initiative; other issues can link to it',
    typeSub: '**Sub-task** – Child of another issue',
    fieldsTitle: 'What you can set',
    fieldsBody:
      'Title, description, assignee, priority, status, and due date are the basics. You can also add labels, components, versions, parent or epic link, time estimates, and attachments. When creating an issue, you can add a **linked issue** and choose the link type (e.g. Blocks, Is blocked by, Relates to, Duplicates). The chosen type is remembered as the default for the next link. Open an issue to edit everything and add comments.',
  },
  backlog: {
    intro:
      'See all issues not in a sprint and manage your sprints. Drag issues to reorder the backlog or into a sprint.',
    b1: 'Drag issues into a sprint to plan work.',
    b2: 'Create sprints and move them from planned → active → completed.',
    b3: 'Move issues back to the backlog if needed.',
    b4:
      'Large backlogs use virtualized scrolling for performance; drag-and-drop uses the same ordering as before.',
  },
  'project-management': {
    intro:
      'Available to admins and project managers. Open **Project Management** from the sidebar to see all projects and a high-level roadmap overview.',
    allTitle: 'All projects',
    allBody:
      'The table lists every project in your organization. You can create a new project, export the list to CSV, and open each project’s board, backlog, roadmap, or docs. Use **Manage members** per project to add people and set roles.',
    archivedBody:
      'Turn on **Show archived** to include archived projects. Org admins and project admins can archive or restore a project from **Actions** — archived projects are hidden from the default list and from most pickers until restored.',
    roadmapTitle: 'Roadmap overview',
    roadmapLead:
      'At the top of the page, the **Roadmap overview** shows, for each project:',
    ro1: '**Current stage** – The latest released version (if any).',
    ro2: '**Next deployment** – The next planned version name.',
    ro3: '**Planned date** – The release date of that version when set.',
    roadmapFoot:
      'Use this to see where each project is and when the next release is planned. Click a project name or **View full roadmap** to open the full cross-project roadmap or a project’s roadmap.',
    roadmapNote:
      'Versions and release dates are configured per project (Admin Settings or project settings). Add versions and set Fix version on issues to populate the roadmap.',
  },
  roadmap: {
    intro:
      'Roadmaps show versions and releases so you can see what’s planned and when. FlowBoard has a per-project roadmap and an org-wide cross-project roadmap.',
    projTitle: 'Project roadmap',
    projLead: 'Open a project and choose **Roadmap** in the sidebar. You’ll see:',
    pr1:
      'A summary card with **Current stage** (latest released version), **Next deployment** (next planned version), and **Planned date** (release date).',
    pr2:
      '**List view** – A timeline of versions with descriptions, start/release dates, and issues (Fix version / Affects version).',
    pr3:
      '**Timeline view** – Versions as bars on a timeline; drag issue badges between versions to change an issue’s Fix version.',
    fixBody:
      'Add versions in your project (or org) and set **Fix version** / Affects version on issues so they appear on the roadmap.',
    crossTitle: 'Cross-project roadmap',
    crossBody:
      'From the sidebar, open **Cross-project roadmap** to see all versions across all projects in one list. Each row shows project, version name, release date, and issue counts (Fix / Affects). Click a project name to open that project’s roadmap.',
    tip: 'Use the roadmap to align stakeholders on where you are and when the next deployment is planned. Set start and release dates on versions for accurate timelines.',
  },
  calendar: {
    body: 'Issues with due dates appear on the calendar. Switch months to plan ahead or spot overdue work.',
  },
  'list-view': {
    intro:
      'A table of all issues in the project. Sort, filter, and bulk-edit status or assignee.',
    lv1: 'Use advanced filters to narrow by status, assignee, label, etc.',
    lv2: 'Select multiple issues to change status, assignee, or delete in one go.',
    lvBulk:
      'Bulk edit updates many issues at once. From a project list you can also set labels, components, fix versions, and resolution.',
    lv3:
      'When the list is large, use **pagination** at the bottom to move between pages and control how many issues appear per page.',
    lv4:
      '**Saved layout** stores filter, search, sort, and column choices in this browser (separate from server-side Saved Filters). Turn on the **SLA** column to see which issues have an active SLA breach.',
    tip: 'List view is best when you need to compare many issues or do bulk updates.',
  },
  'dependency-graph': {
    body: 'A visual map of how issues are linked (blocks, blocked by, relates to). Use it to see impact and critical paths.',
  },
  docs: {
    intro:
      'Each project has a **Docs** area (like Confluence) for specs, runbooks, and meeting notes. Open a project and go to **Docs** in the sidebar.',
    tplTitle: 'Page templates and labels',
    tplBody:
      'When creating or editing a page, you can pick a **template** (e.g. Meeting notes, Spec) and add **labels** (e.g. spec, meeting-notes) to organize and find pages quickly.',
    sidebarTitle: 'Left sidebar',
    sidebarBody:
      'The docs layout shows a tree of all pages on the left. Use **New page** to create a page, and the search box to filter by title. Expand or collapse folders with the chevron; click a page title to open it.',
    pageTitle: 'On the page',
    d1: '**On this page** – Sticky table of contents from H2/H3 headings when viewing (not editing).',
    d2: '**Child pages** – If the page has subpages, they’re listed below the content with links.',
    d3: '**Edit** – Change title, parent, and content. Use the toolbar for formatting, links, and images.',
    d4: '**History** – View and restore previous versions.',
    d5: '**Export** – Download the page as Markdown.',
    d6: 'Comments appear at the bottom; add and read replies there.',
    tip: 'Press **Ctrl + S** in the doc editor to save without leaving the page.',
  },
  reports: {
    intro:
      'From a project, open **Reports** to see velocity, cumulative flow, control charts, epic progress, time-tracking, report builder, and the **Workflow** (Gantt) view. Export data or the workflow chart when you need to share.',
    typesTitle: 'Report types',
    r1: '**Velocity** – Completed work per sprint.',
    r2: '**Cumulative flow** – How issues move through statuses over time.',
    r3: '**Control chart** – Cycle time for completed issues.',
    r4:
      '**Epic report** – Use the **Epic** dropdown to filter by a single epic or view all epics. Export epic data from the export button.',
    r5: '**Time tracking** – Estimates vs time spent by type and by user.',
    r6:
      '**Workflow (Gantt)** – Timeline of issues with due dates: hierarchy (epics → tasks → sub-tasks), progress bars, dependency lines (parent–child and issue links like “blocks”), and milestones (same-day items as diamonds). Use zoom (Day / Week / Month) and **Export as image** to save the chart as PNG. Bar colors use project status colors.',
    r7: '**Report builder** – Group issues by status, type, priority, or assignee.',
    tip: 'Use velocity and cumulative flow to understand team throughput and spot bottlenecks.',
  },
  dashboards: {
    body: 'Go to **Custom Dashboards** to build your own dashboard with widgets (by project, group, or assignee). You can control who sees each dashboard by group or role.',
  },
  'admin-settings': {
    intro:
      'Open **Admin Settings** from the sidebar to manage your organization and project setup.',
    orgTitle: 'Organization',
    orgBody: 'Edit your org name, manage members, and handle pending invites.',
    projCfgTitle: 'Project configuration',
    projCfgBody:
      'Manage **Labels**, **Components**, **Versions**, and **Custom fields** used across projects.',
    tplTitle: 'Templates',
    tplBody:
      '**Project** and **issue** templates set default workflows and fields for new projects or issues.',
    autoTitle: 'Automation',
    autoBody:
      'Create rules that run when issues match conditions (e.g. status change, assignee set). Use triggers and actions to automate repetitive steps. Use the **Edit** (pencil) icon in the table to change a rule’s name, description, trigger, or enabled state; use **Delete** to remove a rule.',
    groupsTitle: 'Groups',
    groupsBody:
      'Create groups, add members, then assign groups to projects (in Manage members) so everyone in the group gets the same project role.',
    rolesTitle: 'Roles & audit log',
    rolesBody: 'Manage custom roles and view the audit log for important changes.',
    systemTitle: 'System (super admin only)',
    systemBody:
      'The **System** tab lists all organizations. You can set each org’s **plan** (Free, Pro, Enterprise) and invite new companies.',
  },
  glossary: {
    intro: 'Short definitions of terms you’ll see in FlowBoard.',
    g1: '**Issue** – A work item: task, story, bug, epic, or sub-task.',
    g2: '**Epic** – A large initiative; other issues can link to it.',
    g3: '**Sprint** – A time-boxed set of issues (e.g. 2 weeks).',
    g4: '**Backlog** – Issues not yet in a sprint.',
    g5: '**Version** – A release or milestone (e.g. v1.0, Q2 Release). Has optional start and release dates; can be marked Released.',
    g6: '**Fix version** – The version an issue is planned to be fixed or delivered in. Shown on the roadmap.',
    g7: '**Affects version** – The version(s) that an issue affects (e.g. for bugs). Also shown on the roadmap.',
    g8: '**Kanban board** – Columns by status; drag cards to update.',
    g9: '**Workflow** – The statuses and transitions (e.g. To Do → In Progress → Done).',
    g10: '**Assignee** – The person responsible for the issue.',
    g11: '**Watchers** – Users who get notified about updates.',
  },
  'subscription-plans': {
    body: 'The Free plan allows a limited number of projects and users; Standard, Premium, and Enterprise allow more (including unlimited). When a limit is reached, creation is blocked until the plan is upgraded. Super admins set each organization’s plan in Admin Settings → System.',
  },
  'api-integrations': {
    intro:
      'FlowBoard exposes a REST API for issues, projects, reports, webhooks, and more. Use your instance’s API base URL with a valid auth token (e.g. from login) for requests.',
    webhooksTitle: 'Webhooks',
    webhooksBody:
      'Org admins can configure outgoing webhooks in **Admin → Webhooks**. Webhooks send HTTP POST payloads on issue and project events; you can optionally set an HMAC secret for verification. To send notifications to Slack, add a webhook with your Slack Incoming Webhook URL (or use Automation rules with the Webhook action).',
    openapiNote:
      'An OpenAPI 3.0 spec is available at **/api/docs/openapi.json** when the backend is running; use it with Swagger UI or other API tools.',
  },
};

export default helpSectionsEn;
